import React, { useState, useEffect } from "react"
import { Container, Row, Col, Card, CardBody, Input, Button } from "reactstrap"
import { withRouter } from "react-router-dom"
import { connect } from "react-redux"
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd"

import * as backend from "../../helpers/backend_helper"
import EditableColumn from "./EditableColumn"
import MwSelect from "../../components/Common/MwSelect"
import Toaster from "../../components/Common/Toaster"
import DeleteAlert from "../../common/ui/customAlert"

// Stable client-side id so react keys / drag ids follow the item across reorders
// (index-based ids would leave a row's edit state on the wrong item after a move).
const uid = () => "u" + Math.random().toString(36).slice(2, 10) + (Date.now() % 100000).toString(36)
const withUids = (groups: any[]) =>
  (groups || []).map((g: any) => ({
    ...g,
    _uid: g._uid || uid(),
    lists: (g.lists || []).map((l: any) => ({ ...l, _uid: l._uid || uid() })),
  }))


const DailyMonitoring = (props: any) => {
  const {
    match: {
      params: { orgId, facId },
    },
  } = props

  const [daily, setDaily] = useState<any[]>([])
  const [seeded, setSeeded] = useState(false)
  const [deleteAlertVisible, setDeleteAlertVisible] = useState(false)
  const [deleteData, setdeleteData] = useState<any>({})

  // add group
  function addGroup() {
    setDaily([...daily, { name: null, lists: [], _uid: uid() }])
  }
  // remove group
  function removeGroup(idx) {
    const cloneList = [...daily]
    cloneList.splice(idx, 1)
    setDaily([...cloneList])
  }

  function addSubList(id) {
    const cloneList = [...daily]
    cloneList[id].lists = [
      ...cloneList[id].lists,
      { name: null, type: "select", _uid: uid() },
    ]
    setDaily([...cloneList])
  }

  function updateGroupValue(e, idx) {
    const cloneList = [...daily]
    cloneList[idx].name = e
    setDaily([...cloneList])
  }

  function updateListValue(e, key, idx, liIndex) {
    const cloneList = [...daily]
    const list = cloneList[idx].lists[liIndex]
    if (list) {
      list[key] = e
    }
    setDaily([...cloneList])
  }

  // Unified drag handler:
  //  - type "group": reorder whole header groups up/down.
  //  - type "point": move a point within its header OR to another header.
  function onDragEnd(result) {
    const { source, destination, type } = result
    if (!destination) return
    if (type === "group") {
      if (source.index === destination.index) return
      const clone = [...daily]
      const [moved] = clone.splice(source.index, 1)
      clone.splice(destination.index, 0, moved)
      setDaily(clone)
      return
    }
    // type "point": droppableId is the group's stable _uid
    const fromG = daily.findIndex(g => g._uid === source.droppableId)
    const toG = daily.findIndex(g => g._uid === destination.droppableId)
    if (fromG < 0 || toG < 0) return
    if (fromG === toG && source.index === destination.index) return
    const clone = [...daily]
    const fromLists = [...clone[fromG].lists]
    const [moved] = fromLists.splice(source.index, 1)
    if (fromG === toG) {
      fromLists.splice(destination.index, 0, moved)
      clone[fromG] = { ...clone[fromG], lists: fromLists }
    } else {
      const toLists = [...clone[toG].lists]
      toLists.splice(destination.index, 0, moved)
      clone[fromG] = { ...clone[fromG], lists: fromLists }
      clone[toG] = { ...clone[toG], lists: toLists }
    }
    setDaily(clone)
  }

  //deleteListValue
  function deleteListValue(idx, liIndex) {
    const cloneList = [...daily]
    const list = cloneList[idx].lists
    list.splice(liIndex, 1)
    setDaily([...cloneList])
  }

  // on onDelete
  const onDelete = async (id, index) => {
    setdeleteData({id:id,index:index})
    // setDeleteId(deleteId)
    toggleDeleteAlert()
  }

  const Submit = () => {
    deleteListValue(deleteData.id,deleteData.index)
    toggleDeleteAlert()
  }

  const toggleDeleteAlert = () => {
    setDeleteAlertVisible(!deleteAlertVisible)
  }


  // Seed local state ONCE from props.form. Re-syncing on every props.form change
  // wipes the user's unsaved edits (e.g. after deleting a row), so only seed the
  // first time the form data arrives.
  useEffect(() => {
    if (props.form && !seeded) {
      setDaily(withUids(props.form))
      setSeeded(true)
    }
  }, [props.form, seeded])

  // on submit
  async function submit() {
    try {
      await backend.updateFactory(orgId, facId, { dailyMonitoringForm: daily })
      props.update()
      Toaster.success("Daily Monitoring Updated Success")
    } catch (e) {
      Toaster.error("Daily Monitoring Updated Failed")
    }
  }

  return (
    <React.Fragment>
      <Container fluid>
        <div style={{ display: "flex", justifyContent: "space-between" }}>
          <h4>Daily Monitoring</h4>
          <button
            onClick={addGroup}
            type="button"
            className="btn btn-primary waves-effect waves-light"
          >
            New Group
          </button>
        </div>
        <br />
        <Row>
          <Col lg={12}>
            <Card>
              <CardBody>
                <DragDropContext onDragEnd={onDragEnd}>
                  <Droppable droppableId="groups" type="group">
                    {gprov => (
                      <ul className="list-unstyled" ref={gprov.innerRef} {...gprov.droppableProps}>
                        {daily.map((daily, idd) => (
                          <Draggable key={daily._uid} draggableId={`group-${daily._uid}`} index={idd}>
                            {gp => (
                              <li ref={gp.innerRef} {...gp.draggableProps}>
                                <div
                                  className="factory-monitoring-group-list"
                                  style={{ textAlign: "start" }}
                                >
                                  <Row className="align-items-center">
                                    <Col md={1}>
                                      <i
                                        className="mdi mdi-drag-vertical"
                                        title="Drag to reorder group"
                                        style={{ cursor: "grab", fontSize: "22px", color: "#1D3A5F" }}
                                        {...gp.dragHandleProps}
                                      />
                                    </Col>
                                    <Col md={9}>
                                      <EditableColumn
                                        value={daily.name}
                                        update={e => updateGroupValue(e, idd)}
                                      />
                                    </Col>
                                    <Col md={1}>
                                      <button
                                        onClick={e => addSubList(idd)}
                                        type="button"
                                        className="btn btn-primary waves-effect waves-light"
                                      >
                                        ADD
                                      </button>
                                    </Col>
                                    <Col md={1}>
                                      <button
                                        onClick={e => removeGroup(idd)}
                                        type="button"
                                        className="btn btn-danger waves-effect waves-light"
                                      >
                                        <i className="mdi mdi-trash-can d-block font-size-16"></i>{" "}
                                      </button>
                                    </Col>
                                  </Row>
                                </div>
                                <Droppable droppableId={daily._uid} type="point">
                                  {(pprov, psnap) => (
                                    <ul
                                      className="list-unstyled"
                                      ref={pprov.innerRef}
                                      {...pprov.droppableProps}
                                      style={{
                                        minHeight: 12,
                                        background: psnap.isDraggingOver ? "#eef7f5" : "transparent",
                                        borderRadius: 8,
                                      }}
                                    >
                                      {daily.lists.map((sublist, subIndex) => (
                                        <Draggable
                                          key={sublist._uid}
                                          draggableId={`point-${sublist._uid}`}
                                          index={subIndex}
                                        >
                                          {dp => (
                                            <li
                                              ref={dp.innerRef}
                                              {...dp.draggableProps}
                                              style={{ padding: "10px", ...dp.draggableProps.style }}
                                            >
                                              <Row className="align-items-center">
                                                <Col lg={1}>
                                                  <i
                                                    className="mdi mdi-drag-vertical"
                                                    title="Drag to reorder / move to another header"
                                                    style={{ cursor: "grab", fontSize: "20px", color: "#8a97a8" }}
                                                    {...dp.dragHandleProps}
                                                  />
                                                </Col>
                                                <Col lg={4}>
                                                  <EditableColumn
                                                    value={sublist.name}
                                                    update={e =>
                                                      updateListValue(e, "name", idd, subIndex)
                                                    }
                                                  />
                                                </Col>
                                                <Col lg={3}>
                                                  <MwSelect
                                                    value={sublist.type}
                                                    placeholder="Type"
                                                    onChange={(val: any) => {
                                                      updateListValue(val, "type", idd, subIndex)
                                                      // "Show Total" only applies to Input checkpoints.
                                                      if (val !== "input") updateListValue(false, "showTotal", idd, subIndex)
                                                    }}
                                                    options={[
                                                      { value: "input", label: "Input" },
                                                      { value: "select", label: "Select" },
                                                    ]}
                                                  />
                                                </Col>
                                                <Col lg={3}>
                                                  {/* Show this checkpoint's total (sum
                                                      across departments) in the report
                                                      header — screen and PDF. Only makes
                                                      sense for numeric "Input" checkpoints,
                                                      not Yes/No "Select" ones. A toggle
                                                      button (theme CSS breaks the native
                                                      checkbox's clickability). */}
                                                  {sublist.type === "input" && (
                                                    <button
                                                      type="button"
                                                      title="Show this checkpoint's total in the report header"
                                                      onClick={() => updateListValue(!sublist.showTotal, "showTotal", idd, subIndex)}
                                                      style={{
                                                        border: `1px solid ${sublist.showTotal ? "#2ea99b" : "#cdd5dd"}`,
                                                        borderRadius: 8,
                                                        padding: "6px 12px",
                                                        fontSize: 12,
                                                        fontWeight: 600,
                                                        cursor: "pointer",
                                                        background: sublist.showTotal ? "#2ea99b" : "#fff",
                                                        color: sublist.showTotal ? "#fff" : "#6b7785",
                                                        width: "100%",
                                                      }}
                                                    >
                                                      {sublist.showTotal ? "✓ Show Total in Header" : "Show Total in Header"}
                                                    </button>
                                                  )}
                                                </Col>
                                                <Col lg={1}>
                                                  <button
                                                    onClick={e => onDelete(idd, subIndex)}
                                                    type="button"
                                                    className="btn btn-danger waves-effect waves-light"
                                                  >
                                                    <i className="mdi mdi-trash-can d-block font-size-16"></i>{" "}
                                                  </button>
                                                </Col>
                                              </Row>
                                            </li>
                                          )}
                                        </Draggable>
                                      ))}
                                      {pprov.placeholder}
                                    </ul>
                                  )}
                                </Droppable>
                              </li>
                            )}
                          </Draggable>
                        ))}
                        {gprov.placeholder}
                      </ul>
                    )}
                  </Droppable>
                </DragDropContext>
                {daily.length > 0 && (
                  <div style={{ display: "flex", justifyContent: "center" }}>
                    <Button onClick={submit} color="success">
                      Submit
                    </Button>
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
      <DeleteAlert
          visible={deleteAlertVisible}
          Title={"Delete Form"}
          Message={"Do you Want to Delete?"}
          toggleAlert={toggleDeleteAlert}
          Submit={Submit}
        />
    </React.Fragment>
  )
}

const mapStateToProps = state => {
  return {}
}

export default withRouter<any, any>(connect<any>(mapStateToProps, {})(DailyMonitoring))
